<script setup lang="ts">
/**
 * 代理商替客戶送換機申請（批108）。
 *
 * 客戶換硬體時，代理商原本完全幫不上忙：換機申請只有客戶自己在 portal 送得出來，而代理商的
 * `change`／`renew` 草稿**刻意不收指紋**（指紋一律從現行證沿用）——那道限制是對的
 * （不能讓代理商替客戶改綁機器），但它同時讓「代理商幫客戶全部處理」斷在這裡。
 *
 * 折衷：**送件開放、核准不開放**。代理商把客戶新主機的 `request.json` 送上來，
 * 我方在〈換機申請〉頁核准。
 *
 * ★ 指紋一律由後端從 `request.json` 解析——這個表單也刻意沒有「直接填指紋」的欄位。
 */
import { computed, ref, watch } from 'vue'

import UiButton from '@/components/UiButton.vue'
import UiModal from '@/components/UiModal.vue'
import { ApiError } from '@/api/client'
import * as reseller from '@/api/reseller'
import type { ResellerLicense } from '@/api/reseller'

const props = defineProps<{ open: boolean; license: ResellerLicense | null }>()
const emit = defineEmits<{ close: []; done: [] }>()

const raw = ref('')
const busy = ref(false)
const error = ref('')
/** 選檔（批171：原生檔案欄位換成按鈕＋檔名，與站內其他上傳一致）。 */
const fileInput = ref<HTMLInputElement | null>(null)
const fileName = ref('')

watch(
  () => props.open,
  (open) => {
    if (!open) return
    raw.value = ''
    error.value = ''
    fileName.value = ''
  },
)

/** 即時解析：讓操作者在按下去之前就看到「這是哪一台機器」。 */
const parsed = computed<{ instance_id?: string; hostname?: string; fingerprint?: number } | null>(
  () => {
    if (!raw.value.trim()) return null
    try {
      const obj = JSON.parse(raw.value) as Record<string, unknown>
      const fp = obj.machine_fingerprint as Record<string, unknown> | undefined
      return {
        instance_id: typeof obj.instance_id === 'string' ? obj.instance_id : undefined,
        hostname: typeof obj.hostname === 'string' ? obj.hostname : undefined,
        fingerprint: fp ? Object.keys(fp).length : 0,
      }
    } catch {
      return null
    }
  },
)

async function pickFile(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  fileName.value = file.name
  raw.value = await file.text()
  input.value = ''
}

async function submit() {
  if (!props.license) return
  let body: Record<string, unknown>
  try {
    body = JSON.parse(raw.value) as Record<string, unknown>
  } catch {
    error.value = '這不是合法的 JSON——請把新主機產生的 request.json 整份貼上。'
    return
  }
  busy.value = true
  error.value = ''
  try {
    await reseller.submitRebind({
      customer_id: props.license.customer_id,
      old_license_id: props.license.license_id,
      new_request: body,
    })
    emit('done')
    emit('close')
  } catch (e) {
    const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : null
    const obj = (d ?? {}) as { code?: string; message?: string }
    error.value =
      obj.code === 'hosted_instance'
        ? (obj.message ?? '這是代管站，換主機由中台處理。')
        : (obj.message ?? '送出換機申請失敗')
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <UiModal :open="open" title="申請換機" width="560px" @close="emit('close')">
    <p class="hint">
      客戶換了機器時用這個。請客戶在<strong>新主機</strong>上執行安裝程式產生的
      <code class="mono">license/request.json</code>，整份貼進來或選檔上傳。
      <strong>核准仍由愛+1 處理</strong>——送出後我方會在〈換機申請〉看到。
    </p>

    <div class="pick">
      <input ref="fileInput" type="file" accept=".json,application/json" hidden @change="pickFile" />
      <UiButton variant="secondary" @click="fileInput?.click()">選擇 request.json…</UiButton>
      <span class="file-name">{{ fileName || '未選擇檔案（也可以直接貼在下面）' }}</span>
    </div>
    <textarea
      v-model="raw"
      rows="8"
      class="raw"
      placeholder='把新主機的 request.json 貼在這裡，例如 {"schema_version":1,"instance_id":"…","machine_fingerprint":{…}}'
    ></textarea>

    <div v-if="parsed" class="parsed">
      <div><span class="k">新主機 ID</span><code class="mono">{{ parsed.instance_id ?? '—' }}</code></div>
      <div><span class="k">主機名稱</span>{{ parsed.hostname ?? '—' }}</div>
      <div><span class="k">硬體錨點</span>{{ parsed.fingerprint }} 項</div>
    </div>
    <p v-else-if="raw.trim()" class="warn-text">還不是合法的 JSON（貼完整份檔案內容）。</p>

    <p v-if="error" class="warn-text">{{ error }}</p>

    <template #foot>
      <UiButton @click="emit('close')">取消</UiButton>
      <UiButton variant="primary" :loading="busy" :disabled="!parsed" @click="submit">
        送出申請
      </UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
.pick {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  min-width: 0;
  margin: 10px 0 8px;
}
.file-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--fs-12);
  color: var(--fg-3);
}
.raw {
  width: 100%;
  min-width: 0;
  font-family: var(--font-mono, monospace);
  font-size: var(--fs-12, 12px);
}
.parsed {
  margin-top: 10px;
  display: grid;
  gap: 4px;
}
.parsed .k {
  display: inline-block;
  width: 6em;
  color: var(--fg-3);
}
.warn-text {
  margin-top: 8px;
  color: var(--warning);
}
</style>
